import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { NAV_LINKS, MOBILE_LINKS } from "@/data/site";
import PillButton from "@/components/PillButton";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled || open ? "bg-white/90 backdrop-blur-md border-b border-hairline" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-[64px] w-full max-w-[1440px] items-center justify-between px-5 sm:h-[72px] sm:px-8 lg:h-[76px] lg:pl-[56px] lg:pr-[40px] xl:pl-[80px]">
        <Link
          to="/"
          data-testid="header-logo-link"
          className="font-display text-[17px] uppercase tracking-[0.16em] text-ink sm:text-[18px]"
        >
          Stephanie Roxanne
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.href}
              to={l.href}
              data-testid={`header-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `font-display text-[13px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                  isActive ? "text-black" : "text-charcoal hover:text-black"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <PillButton href="/episodes" dataTestId="header-listen-button" className="!px-7 !py-2 !text-[14px]">
            Listen
          </PillButton>
        </nav>

        <button
          type="button"
          data-testid="header-mobile-menu-button"
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((v) => !v)}
          className="relative z-50 flex h-10 w-10 flex-col items-center justify-center gap-[5px] lg:hidden"
        >
          <span className={`h-[1.5px] w-6 bg-ink transition-transform duration-300 ${open ? "translate-y-[6.5px] rotate-45" : ""}`} />
          <span className={`h-[1.5px] w-6 bg-ink transition-opacity duration-300 ${open ? "opacity-0" : ""}`} />
          <span className={`h-[1.5px] w-6 bg-ink transition-transform duration-300 ${open ? "-translate-y-[6.5px] -rotate-45" : ""}`} />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 top-0 z-40 bg-linen lg:hidden"
          >
            <div className="flex h-full flex-col justify-center px-8 pt-16">
              {MOBILE_LINKS.map((l, i) => (
                <motion.div
                  key={l.href}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55, delay: 0.06 * i, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={l.href}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="block border-b border-ink/10 py-4 font-display text-[28px] uppercase tracking-[0.06em] text-ink"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
